import React from 'react';
import styled from 'styled-components';
import { LimitPriceType, PriceReference } from '../../types/preset';
import Tooltip from './Tooltip';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ExecutionTimeNotice = styled.div`
  background: #1e3a8a;
  border: 1px solid #3b82f6;
  border-radius: 6px;
  padding: 8px 12px;
  margin-top: 8px;
  font-size: 12px;
  color: #93c5fd;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NoticeIcon = styled.span`
  color: #60a5fa;
  font-weight: bold;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #333;
  border-radius: 4px;
  background: #1a1a1a;
  color: #fff;
  font-size: 14px;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #333;
  border-radius: 4px;
  background: #1a1a1a;
  color: #fff;
  font-size: 14px;
  width: 120px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &::placeholder {
    color: #666;
  }
`;

const Label = styled.label`
  font-size: 14px;
  color: #ccc;
  min-width: 80px;
`;

interface PriceTypeSelectorProps {
  priceType: LimitPriceType;
  onPriceTypeChange: (type: LimitPriceType) => void;
  priceOffset?: number | null;
  onPriceOffsetChange?: (offset: number | null) => void;
  priceReference?: PriceReference;
  onPriceReferenceChange?: (reference: PriceReference) => void;
  label?: string;
  showTooltip?: boolean;
  tooltipContent?: string;
}

const PriceTypeSelector: React.FC<PriceTypeSelectorProps> = ({
  priceType,
  onPriceTypeChange,
  priceOffset,
  onPriceOffsetChange,
  priceReference = PriceReference.LAST,
  onPriceReferenceChange,
  label = "Price Type",
  showTooltip = true,
  tooltipContent
}) => {
  const defaultTooltip = `
    • ABSOLUTE: Use exact price value
    • BEST_BID: Place relative to best bid + offset (calculated at execution)
    • BEST_ASK: Place relative to best ask + offset (calculated at execution)
    • RELATIVE: Apply as offset from price reference (calculated at execution)
    • PERCENT: Apply as percentage of price reference (calculated at execution)
  `;

  const showOffsetField = priceType === LimitPriceType.BEST_BID || priceType === LimitPriceType.BEST_ASK || priceType === LimitPriceType.RELATIVE;
  const showPercentField = priceType === LimitPriceType.PERCENT;
  const showReferenceField = priceType === LimitPriceType.RELATIVE || priceType === LimitPriceType.PERCENT;
  
  // Show execution time notice for dynamic price types
  const isDynamicPriceType = priceType !== LimitPriceType.ABSOLUTE;

  return (
    <Container>
      <Row>
        <Label>{label}:</Label>
        <Select
          value={priceType}
          onChange={(e) => onPriceTypeChange(e.target.value as LimitPriceType)}
        >
          <option value={LimitPriceType.ABSOLUTE}>Absolute Price</option>
          <option value={LimitPriceType.BEST_BID}>Best Bid + Offset</option>
          <option value={LimitPriceType.BEST_ASK}>Best Ask + Offset</option>
          <option value={LimitPriceType.RELATIVE}>Offset to Reference</option>
          <option value={LimitPriceType.PERCENT}>Percentage of Reference</option>
        </Select>
        {showTooltip && (
          <Tooltip content={tooltipContent || defaultTooltip}>
            <span style={{ color: '#666', cursor: 'help' }}>ⓘ</span>
          </Tooltip>
        )}
      </Row>

      {isDynamicPriceType && (
        <ExecutionTimeNotice>
          <NoticeIcon>⏱</NoticeIcon>
          <span>
            Dynamic price will be calculated using live market data when the preset is executed
          </span>
        </ExecutionTimeNotice>
      )}

      {showOffsetField && onPriceOffsetChange && (
        <Row>
          <Label>
            {priceType === LimitPriceType.BEST_BID || priceType === LimitPriceType.BEST_ASK 
              ? 'Offset:' 
              : 'Offset Amount:'}
          </Label>
          <Input
            type="number"
            step="0.01"
            placeholder={
              priceType === LimitPriceType.BEST_BID || priceType === LimitPriceType.BEST_ASK 
                ? '0.00' 
                : 'Enter offset amount'
            }
            value={priceOffset || ''}
            onChange={(e) => onPriceOffsetChange(e.target.value ? parseFloat(e.target.value) : null)}
          />
          <span style={{ color: '#666', fontSize: '12px' }}>
            {priceType === LimitPriceType.BEST_BID ? 'Added to best bid at execution' : 
             priceType === LimitPriceType.BEST_ASK ? 'Added to best ask at execution' :
             'Offset from reference price at execution'}
          </span>
        </Row>
      )}

      {showPercentField && onPriceOffsetChange && (
        <Row>
          <Label>Percentage:</Label>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="100"
            placeholder="Enter percentage"
            value={priceOffset || ''}
            onChange={(e) => onPriceOffsetChange(e.target.value ? parseFloat(e.target.value) : null)}
          />
          <span style={{ color: '#666', fontSize: '12px' }}>
            % of reference price at execution
          </span>
        </Row>
      )}

      {showReferenceField && onPriceReferenceChange && (
        <Row>
          <Label>Reference:</Label>
          <Select
            value={priceReference}
            onChange={(e) => onPriceReferenceChange(e.target.value as PriceReference)}
          >
            <option value={PriceReference.LAST}>Last Price</option>
            <option value={PriceReference.MARK}>Mark Price</option>
            <option value={PriceReference.INDEX}>Index Price</option>
            <option value={PriceReference.MID}>Mid Price</option>
          </Select>
          <Tooltip content="Price reference used for RELATIVE and PERCENT calculations at execution time">
            <span style={{ color: '#666', cursor: 'help' }}>ⓘ</span>
          </Tooltip>
        </Row>
      )}
    </Container>
  );
};

export default PriceTypeSelector; 